"use client";

import React, { useState } from 'react';
import { useGame } from '@/context/GameContext';
import {Users, UserPlus, Play, AlertCircle, Wand2, Loader2, Brain} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const ADJECTIVES = [
  "swift", "brave", "loyal", "bright", "cool", 
  "smart", "quick", "silent", "mighty", "golden",
  "magic", "wild", "gentle", "lucky", "happy"
];

const NOUNS = [
  "lion", "wolf", "eagle", "falcon", "deer", 
  "bear", "tiger", "shark", "dragon", "phoenix",
  "star", "storm", "comet", "bolt", "river"
];

export default function Lobby() {
  const { joinAiGame, joinQueue, createCustomGame, joinCustomGame, isConnected } = useGame();
  const [loadingMode, setLoadingMode] = useState<'AI' | 'QUEUE' | 'CREATE' | 'JOIN' | null>(null);
  const [name, setName] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('fjalafundit_last_name') || '';
    }
    return '';
  });
  const [error, setError] = useState<string | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [customGameId, setCustomGameId] = useState('');
  const [showJoinCustom, setShowJoinCustom] = useState(false);

  // Check for join parameter in URL and handle auto-join
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const joinId = params.get('join');
    if (joinId) {
      const upperId = joinId.toUpperCase();
      setCustomGameId(upperId);
      setShowJoinCustom(true);
      
      // Auto-join if name exists and is valid
      const storedName = localStorage.getItem('fjalafundit_last_name');
      if (storedName && validateName(storedName) === null) {
        // Trigger join logic directly
        const performAutoJoin = async () => {
          setLoadingMode('JOIN');
          try {
            const result = await joinCustomGame(upperId, storedName.trim());
            if (result && !result.success) {
              setLoadingMode(null);
              const code = result.errorCode || result.error?.code || result.code;
              if (code === '404_0GNF') setJoinError('Dhoma nuk u gjet!');
              else setJoinError('Kodi nuk është i saktë!');
            }
          } catch (e) {
            setLoadingMode(null);
          }
        };
        performAutoJoin();
      }

      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const generateRandomName = () => {
    const adj = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
    const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)];
    const generatedName = `${adj}-${noun}`.toLowerCase();
    setName(generatedName);
    localStorage.setItem('fjalafundit_last_name', generatedName);
    setError(null);
  };

  const validateName = (val: string) => {
    const trimmed = val.trim();
    if (!trimmed) return 'Ju lutem shkruani një emër!';
    if (trimmed.length < 2) return 'Emri duhet të ketë të paktën 2 shkronja!';
    if (trimmed.length > 50) return 'Emri është shumë i gjatë (maksimumi 50)!';
    if (!/^[a-zA-Z0-9ëëçÇ\- ]+$/.test(trimmed)) return 'Emri nuk duhet të ketë simbole!';
    return null;
  };

  const saveAndJoin = async (mode: 'AI' | 'QUEUE' | 'CREATE', callback: (n: string) => Promise<void>) => {
    const err = validateName(name);
    if (err) return setError(err);
    
    setError(null);
    setLoadingMode(mode);
    const trimmedName = name.trim();
    localStorage.setItem('fjalafundit_last_name', trimmedName);
    
    try {
      await callback(trimmedName);
    } catch (e) {
      setLoadingMode(null);
    }
  };

  const handleAiPlay = () => saveAndJoin('AI', joinAiGame);
  const handleQueuePlay = () => saveAndJoin('QUEUE', joinQueue);
  const handleCreateCustom = () => saveAndJoin('CREATE', createCustomGame);

  const handleJoinCustom = async () => {
    const nameErr = validateName(name);
    if (nameErr) return setError(nameErr);
    
    if (!customGameId.trim()) {
      return setJoinError('Ju lutem shkruani kodin e lojës!');
    }
    
    setError(null);
    setJoinError(null);
    setLoadingMode('JOIN');
    const trimmedName = name.trim();
    localStorage.setItem('fjalafundit_last_name', trimmedName);
    
    try {
      const result = await joinCustomGame(customGameId.trim(), trimmedName);
      if (result && !result.success) {
        setLoadingMode(null);
        // Map specific error codes to Albanian
        const code = result.errorCode || result.error?.code || result.code;
        if (code === '404_0GNF') {
          setJoinError('Dhoma nuk u gjet! Kontrolloni kodin.');
        } else if (code === '400_NACR') {
          setJoinError('Nuk jeni i autorizuar për këtë veprim.');
        } else {
          // Use result.message if it exists, otherwise a generic Albanian message
          const msg = result.message || result.error?.message || 'Kodi i lojës nuk është i saktë!';
          setJoinError(msg === 'The requested game was not found.' ? 'Dhoma nuk u gjet! Kontrolloni kodin.' : msg);
        }
      }
    } catch (e) {
      setLoadingMode(null);
      setJoinError('Pati një problem gjatë hyrjes në lojë.');
    }
  };

  if (!isConnected) {
    return (
      <div className="flex flex-col items-center justify-center space-y-4">
         <div className="w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full animate-spin" />
         <p className="font-black handwritten text-xl">Duke u lidhur me botën...</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md bg-card doodle-border p-4 md:p-12 shadow-doodle-xl relative overflow-hidden max-h-full overflow-y-auto no-scrollbar">
      <div className="text-center space-y-1 mb-4 md:mb-8">
        <h1 className="text-2xl md:text-4xl font-black text-foreground handwritten">
          <span className="text-pink-500">FJALA</span>
          <span className="text-sky-500">FUNDIT</span>
        </h1>
        <p data-nosnippet className="font-bold handwritten text-slate-500 text-xs md:text-base">Zgjedhni mënyrën e lojës</p>
      </div>

      <div data-nosnippet className="space-y-3 md:space-y-4">
        <div className="relative group">
          <div className="relative">
            <input
              id="playerName"
              name="playerName"
              type="text"
              placeholder="Shkruaj emrin tënd..."
              value={name}
              maxLength={50}              onChange={(e) => {
                setName(e.target.value);
                if (error) setError(null);
              }}
              className={`w-full bg-muted doodle-border-soft p-3 md:p-4 pr-12 md:pr-14 font-black handwritten text-lg md:text-xl outline-none focus:bg-card transition-all ${
                error && name ? 'border-rose-400 bg-rose-50' : ''
              }`}
            />
            <button
              onClick={generateRandomName}
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition-all active:scale-90 group/btn"
              title="Gjenero emër rastësor"
            >
              <Wand2 className="w-5 h-5 md:w-6 md:h-6 group-hover/btn:rotate-12 transition-transform" />
            </button>
          </div>
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex items-center gap-1.5 mt-1.5 px-1.5 text-rose-500 font-bold handwritten text-xs md:text-sm"
              >
                <AlertCircle className="w-3.5 h-3.5 md:w-4 md:h-4" />
                <span>{error}</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="grid grid-cols-1 gap-3 md:gap-4">
          <button
            onClick={handleAiPlay}
            disabled={loadingMode !== null}
            className="group relative w-full bg-sky-50 hover:bg-sky-100 doodle-border-soft border-sky-200 p-3 md:p-4 flex items-center gap-3 md:gap-4 transition-all hover:-translate-y-1 hover:shadow-md disabled:opacity-70 disabled:hover:translate-y-0 disabled:hover:shadow-none"
          >
            <div className="p-2.5 md:p-3 bg-sky-400 rounded-lg group-hover:rotate-12 transition-transform shadow-sm">
              {loadingMode === 'AI' ? <Loader2 className="w-5 h-5 md:w-6 md:h-6 text-white animate-spin" /> : <Brain className="w-5 h-5 md:w-6 md:h-6 text-white" />}
            </div>
            <div className="text-left">
              <div className="font-black text-sky-700 handwritten text-base md:text-lg uppercase">
                {loadingMode === 'AI' ? 'Përgatitje...' : 'Luaj me Gjeniun'}
              </div>
              <div className="text-[10px] md:text-xs font-bold text-sky-500/70 uppercase">Solo vs AI</div>
            </div>
          </button>

          <button
            onClick={handleQueuePlay}
            disabled={loadingMode !== null}
            className="group relative w-full bg-rose-50 hover:bg-rose-100 doodle-border-soft border-rose-200 p-3 md:p-4 flex items-center gap-3 md:gap-4 transition-all hover:-translate-y-1 hover:shadow-md disabled:opacity-70 disabled:hover:translate-y-0 disabled:hover:shadow-none"
          >
            <div className="p-2.5 md:p-3 bg-rose-400 rounded-lg group-hover:-rotate-12 transition-transform shadow-sm">
              {loadingMode === 'QUEUE' ? <Loader2 className="w-5 h-5 md:w-6 md:h-6 text-white animate-spin" /> : <Users className="w-5 h-5 md:w-6 md:h-6 text-white" />}
            </div>
            <div className="text-left">
              <div className="font-black text-rose-700 handwritten text-base md:text-lg uppercase">
                {loadingMode === 'QUEUE' ? 'Duke hyrë...' : 'Gjej Kundërshtar'}
              </div>
              <div className="text-[10px] md:text-xs font-bold text-rose-500/70 uppercase">PvP Matchmaking</div>
            </div>
          </button>

          <div className="flex gap-2">
            <button
              onClick={handleCreateCustom}
              disabled={loadingMode !== null}
              className="flex-1 group bg-amber-50 hover:bg-amber-100 doodle-border-soft border-amber-200 p-3 md:p-4 flex flex-col items-center justify-center gap-1.5 md:gap-2 transition-all hover:-translate-y-1 hover:shadow-md disabled:opacity-70 disabled:hover:translate-y-0 disabled:hover:shadow-none"
            >
              <div className="p-2 bg-amber-400 rounded-lg shadow-sm">
                {loadingMode === 'CREATE' ? <Loader2 className="w-4 h-4 md:w-5 md:h-5 text-white animate-spin" /> : <UserPlus className="w-4 h-4 md:w-5 md:h-5 text-white" />}
              </div>
              <span className="font-black text-amber-700 handwritten text-xs md:text-sm uppercase">
                Sfido
              </span>
            </button>

            <button
              onClick={() => {
                setShowJoinCustom(!showJoinCustom);
                setError(null);
              }}
              disabled={loadingMode !== null}
              className="flex-1 group bg-emerald-50 hover:bg-emerald-100 doodle-border-soft border-emerald-200 p-3 md:p-4 flex flex-col items-center justify-center gap-1.5 md:gap-2 transition-all hover:-translate-y-1 hover:shadow-md disabled:opacity-70 disabled:hover:translate-y-0 disabled:hover:shadow-none"
            >
              <div className="p-2 bg-emerald-400 rounded-lg shadow-sm">
                <Play className="w-4 h-4 md:w-5 md:h-5 text-white" />
              </div>
              <span className="font-black text-emerald-700 handwritten text-xs md:text-sm uppercase">Bashkohu</span>
            </button>
          </div>
        </div>

        {showJoinCustom && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-3 md:mt-4 p-3 md:p-4 bg-slate-100 doodle-border-soft space-y-3 shadow-inner"
          >
            <div className="space-y-1.5 md:space-y-2">
              <input
                type="text"
                placeholder="Vendos kodin e lojës..."
                value={customGameId}
                disabled={loadingMode !== null}
                onChange={(e) => {
                  setCustomGameId(e.target.value.toUpperCase());
                  if (joinError) setJoinError(null);
                }}
                className={cn(
                  "w-full bg-card doodle-border-soft p-2.5 md:p-3 font-bold handwritten outline-none focus:ring-2 focus:ring-foreground/10 disabled:opacity-50 transition-colors text-sm md:text-base",
                  joinError ? "border-rose-400 bg-rose-50" : ""
                )}
              />
              
              <AnimatePresence>
                {joinError && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="flex items-center gap-1.5 px-1 text-rose-500 font-bold handwritten text-[10px] md:text-xs overflow-hidden"
                  >
                    <AlertCircle className="w-3 md:w-3.5 h-3 md:h-3.5 shrink-0" />
                    <span>{joinError}</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <button
              onClick={handleJoinCustom}
              disabled={loadingMode !== null}
              className="w-full bg-border-main text-white py-2.5 md:py-3 font-black handwritten uppercase hover:bg-slate-800 transition-all active:scale-95 shadow-md disabled:opacity-70 flex items-center justify-center gap-2 text-sm md:text-base"
            >
              {loadingMode === 'JOIN' && <Loader2 className="w-4 md:w-5 h-4 md:h-5 animate-spin" />}
              {loadingMode === 'JOIN' ? 'Duke hyrë...' : 'Hyr në Lojë'}
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
